import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useHistory, useLocation } from "react-router-dom";
import "../../assets/css/ui-mdtw.min.css";
import "../../assets/css/seleccion-redesign.min.css";
import "../../assets/css/page-ui-recluiting.min.css";
import "../../assets/css/loading.min.css";
import "../../assets/css/button-generico.min.css";
import "../../assets/css/sub-menu.min.css";
import IPage from "../../interfaces/page";
import logging from "../../config/logging";
import MainTopbar from "../layout/app/MainTopbar";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "../../config/authConfig";
import { Loading } from "../ui/microsoft-auth/msal-template/Loading";
import { ErrorComponent } from "../ui/microsoft-auth/msal-template/ErrorComponent";
import TablaPostulaciones from "./Postulaciones/TablaPostulaciones";
import PanelMetricas from "./Postulaciones/PanelMetricas";
import PanelExperimentoAB from "./Postulaciones/PanelExperimentoAB";
import PanelPrefiltrado from "./Postulaciones/prefiltrado/PanelPrefiltrado";
import { isPostulacionesViewerProfile } from "../../config/twMarketingAccess";
import { FILTER_PROFILE } from "../../diccionary/Diccionary";

type ActiveTab = "postulaciones" | "formularios" | "prefiltrado" | "metricas" | "experimento";

const isActiveTab = (v: string | null): v is ActiveTab =>
  v === "postulaciones" || v === "formularios" || v === "prefiltrado" || v === "metricas" || v === "experimento";

// La pestaña del experimento A/B es exclusiva del perfil adm_ti.
const canSeeTab = (tab: ActiveTab, profile: string): boolean =>
  tab !== "experimento" || profile === FILTER_PROFILE.adm_ti;

const TAB_CONFIG: Record<ActiveTab, { label: string; title: string; subtitle: string }> = {
  postulaciones: {
    label: "Postulaciones",
    title: "Postulaciones",
    subtitle: "Candidatos que han postulado a través del formulario público de registro.",
  },
  formularios: {
    label: "Formularios",
    title: "Formularios",
    subtitle: "Gestiona los formularios de registro público para candidatos.",
  },
  prefiltrado: {
    label: "Prefiltrado",
    title: "Prefiltrado por cliente",
    subtitle: "Postulaciones disponibles evaluadas contra las condiciones básicas de cada cliente y sede.",
  },
  metricas: {
    label: "Métricas",
    title: "Métricas",
    subtitle: "Seguimiento de sesiones, conversiones y actividad en formularios y landing.",
  },
  experimento: {
    label: "Experimento A/B",
    title: "Experimento A/B",
    subtitle: "Métricas de los formularios que participan en el A/B testing.",
  },
};

const RegistroPostulaciones: React.FC<IPage> = (props) => {
  const history  = useHistory();
  const location = useLocation();
  const [profile] = useState<string>(() => window.sessionStorage.getItem("profile") ?? "");
  const isPostulacionesViewer = isPostulacionesViewerProfile(profile);

  const [itemMenu, setItemMenu] = useState<string | undefined>("postulaciones");
  const [itemMenuFilter, setItemMenuFilter] = useState<string>("");

  // URL is the single source of truth for the active tab
  const tabParam  = new URLSearchParams(location.search).get("tab");
  const defaultTab: ActiveTab = isPostulacionesViewer ? "formularios" : "postulaciones";
  const activeTab: ActiveTab = isActiveTab(tabParam) && canSeeTab(tabParam, profile) ? tabParam : defaultTab;

  const setActiveTab = (tab: ActiveTab) => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("tab") === tab) return;
    sp.set("tab", tab);
    history.replace({ pathname: location.pathname, search: `?${sp.toString()}` });
  };

  // make sure ?tab=… is always present so reloads land on the same tab
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const requestedTab = sp.get("tab");
    if (!isActiveTab(requestedTab) || !canSeeTab(requestedTab, profile)) {
      sp.set("tab", defaultTab);
      history.replace({ pathname: location.pathname, search: `?${sp.toString()}` });
    }
  }, [defaultTab, history, location.pathname, location.search, profile]);

  useEffect(() => {
    logging.info(`Loading ${props.name}`);
  }, [props]);

  const onToggleMenuClick = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  const authRequest = { ...loginRequest };

  // Disparador oculto: 20 clics en el logo revelan el botón "Sincronizar
  // registros" sobre la tabla. El contador vive solo en memoria, así que se
  // reinicia al recargar o salir de la página.
  const LOGO_CLICKS_TO_UNLOCK = 20;
  const logoClicksRef = useRef<number>(0);
  const [syncUnlocked, setSyncUnlocked] = useState<boolean>(false);
  const handleLogoClick = () => {
    if (syncUnlocked) return;
    logoClicksRef.current += 1;
    if (logoClicksRef.current >= LOGO_CLICKS_TO_UNLOCK) {
      setSyncUnlocked(true);
    }
  };

  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
        errorComponent={ErrorComponent}
        loadingComponent={Loading}
      >
        {profile ? (
          <MainTopbar
            profile={profile}
            onToggleMenuClick={onToggleMenuClick}
            active={"C"}
            itemMenu={itemMenu}
            setItemMenu={setItemMenu}
            setItemMenuFilter={setItemMenuFilter}
            onLogoClick={handleLogoClick}
          />
        ) : (
          ""
        )}

        <div className="container-redesign-seleccion">
          <div className="content-redesign-seleccion">

            {/* ── Tabs row (upper right, same style as Selección Masiva) ───── */}
            <section className="content-submenu">
              <div />
              <div className="sub-nav-tabs">
                {/* TODO: Métricas ocultas temporalmente — ver PENDIENTE_METRICAS.md */}
              {(Object.keys(TAB_CONFIG) as ActiveTab[]).filter((t) => t !== "metricas" && canSeeTab(t, profile)).map((tab) => {
                  const isActive = activeTab === tab;
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className="sub-nav-tab"
                      style={{ border: "none", background: "none" }}
                    >
                      {isActive && (
                        <motion.div
                          className="sub-nav-pill"
                          layoutId="post-subnav-pill"
                          transition={{ type: "spring", stiffness: 380, damping: 32 }}
                        />
                      )}
                      <span className={`sub-nav-label${isActive ? " active" : ""}`}>
                        {TAB_CONFIG[tab].label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ── Title (changes with active tab) ──────────────────────────── */}
            <div
              style={{
                width: "100%",
                textAlign: "center",
                padding: "16px 0 8px",
              }}
            >
              <h1
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#48566b",
                  margin: 0,
                }}
              >
                {TAB_CONFIG[activeTab].title}
              </h1>
            </div>

            {activeTab === "metricas" ? (
              <PanelMetricas />
            ) : activeTab === "experimento" ? (
              <PanelExperimentoAB />
            ) : activeTab === "prefiltrado" ? (
              <PanelPrefiltrado />

            ) : (
              <TablaPostulaciones activeTab={activeTab} syncUnlocked={syncUnlocked} />
            )}
          </div>
        </div>
      </MsalAuthenticationTemplate>
    </>
  );
};

export default RegistroPostulaciones;
